import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showMessage } from "app/store/actions/message.actions";
import { getError } from "utils/errorutil";
import { Field } from "formik";
import * as Yup from "yup";
import { isEmpty, map, some } from "lodash";

import Form from "components/form";
import { getEnableAutoGPLetterGeneration,
  getEnableLetterTemplateOnlyGenerateForReasonForRejection,
  getGPLetterAcceptFormat,
  getGPLetterOutputTypes,
  getReferralWorkflowStateSettings } from "app/auth/store/reducers/system-configuration";
import { AutoComplete, CheckboxInput, SelectInput, TextInput } from "components/inputs";
import AttachmentUploadField from "components/inputs/attachment-upload";
import { updateGPLetterAttachment,
  updateGPLetterTemplate,
  uploadGPLetterAttachment } from "app/main/orgUnits/actions/orgUnit.actions";
import { getCodeSetValuesByTypeCode } from "../../codeSet/reducers/codeSet.reducers";
import { rejectedWorkflowState } from "../../referrals/helpers/workflow-state-names";
import { DisplayNameLabel,
  ExternalLetterTemplateCodeLabel,
  FileNameLabel,
  FileTypeLabel,
  PatientSMSLabel } from "../constants/orgUnit-gp-letter";

const autoGenerateForReferralStatusesLabel = "Auto generate letter on referral status";
const autoGenerateForReasonsForRejectionCodesLabel = "Only auto generate for these Reason For Rejection Codes";
const autoGenerateNoPatientSMSConsentOnlyLabel = "Only auto generate letter for patients without SMS";
const includeInHL7ReferralMessagesLabel = "Include letter in REF_I12/REF_I13 messages";
const excludeFromPrintedReferralLabel = "Exclude From Printed Referral";

const schema = Yup.object().shape({
  attachment: Yup.object().shape({
    fileName: Yup.string().required("Please choose a file"),
    gpLetterTemplateType: Yup.string().nullable(),
    displayName: Yup.string().required(`${DisplayNameLabel} is required`).max(200, `${DisplayNameLabel} can only be up to 200 characters.`).nullable(),
    outputType: Yup.string().required(`${FileTypeLabel} is required`).nullable(),
    patientSMS: Yup.boolean().nullable(),
    autoGenerateForReasonsForRejectionCodes: Yup.array()
      .of(Yup.string())
      .max(54, "maximum 54 reasons for rejection codes")
      .nullable(),
  }),
});

const OrgUnitGPLetterTemplateForm = ({
  currentAttachment = null,
  onSucceed,
  orgUnitId,
  editAttachment = false,
  ...other
}) => {
  const dispatch = useDispatch();
  const formats = useSelector(getGPLetterAcceptFormat);
  const gpLetterOutputTypes = useSelector(getGPLetterOutputTypes);
  const [disabledActions, setDisabledActions] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showReasonsForRejectionCodesField, setShowReasonsForRejectionCodesField] = useState(false);
  const enabledAttachment = editAttachment || currentAttachment === null;
  const referralWorkflowStateSettings = useSelector(getReferralWorkflowStateSettings);
  const enableAutoGPLetterGeneration = useSelector(getEnableAutoGPLetterGeneration);
  const enableLetterTemplateOnlyGenerateForReasonForRejection = useSelector(getEnableLetterTemplateOnlyGenerateForReasonForRejection);
  const autoGeneratedTypes = map(referralWorkflowStateSettings, x => ({ value: x.workflowState, label: x.label }));
  const rejectionReasons = useSelector(state => getCodeSetValuesByTypeCode(state, "ReasonForRejection"));
  const autoGeneratedRejectionReasons = map(rejectionReasons?.codeSetValues, x => ({ value: x.id, label: x.displayName }));

  useEffect(() => {
    setDisabledActions(uploading || editAttachment);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  const handleSubmit = ({ attachment }, { setSubmitting, setErrors }) => {
    dispatch(updateGPLetterTemplate(orgUnitId, attachment)).then(response => {
      setSubmitting(false);
      if (response.error !== true) {
        onSucceed();
      } else {
        setErrors(response.payload);
      }
    });
  };

  let { attachment } = schema.cast();

  if (currentAttachment) {
    attachment = schema.cast(currentAttachment);
  }

  const displayApiError = (response, setStatus) => {
    setStatus({ apiErrors: response.payload.message });
    const errorMessage = getError(response);
    dispatch(showMessage({
      title: errorMessage.title,
      variant: "error",
      message: errorMessage.message,
      anchorOrigin: { vertical: "bottom", horizontal: "center" },
    }));
    setUploading(false);
  };

  // Check if the selected values include the rejected workflow state
  const includesRejectedStatus = selectedValues => some(
    // chosenValues data format example:
    // [[
    //     {
    //         "value": "Processing",
    //         "label": "Processing"
    //     },
    //     {
    //         "value": "Accepted",
    //         "label": "Accepted"
    //     },
    // ]]

    selectedValues,
    x => x.value === rejectedWorkflowState,
  );

  // This useEffect checks the initial values of autoGenerateForReferralStatuses
  // to determine whether to show the reasons for rejection field when the form
  // first loads
  useEffect(() => {
    // Look for rejectedWorkflowState in the initial values
    // Note: autoGenerateForReferralStatuses is an array of strings
    if (currentAttachment?.autoGenerateForReferralStatuses?.includes(rejectedWorkflowState)) {
      setShowReasonsForRejectionCodesField(true);
    }
  }, [currentAttachment]);

  return (
    <Form
      initialStatus={{ apiErrors: null }}
      initialValues={{ attachment }}
      validationSchema={schema}
      onSubmit={handleSubmit}
      contentProps={other}
      disabledActions={disabledActions}
      content={({ setFieldValue, setStatus, values }) => (
        <>
          {!editAttachment
          && (
          <>
            <Field
              name="attachment.fileName"
              component={TextInput}
              label={FileNameLabel}
              disabled
            />
            <Field
              name="attachment.displayName"
              component={TextInput}
              label={DisplayNameLabel}
              disabled={editAttachment}
              required
            />
            <Field
              name="attachment.gpLetterTemplateType"
              component={TextInput}
              label={ExternalLetterTemplateCodeLabel}
            />
          </>
          )}
          {enabledAttachment && (
            <Field
              name="attachment.fileName"
              component={AttachmentUploadField}
              label="Attachment"
              icon="attachment"
              acceptFormat={formats}
              loading={uploading}
              required
              onChange={file => {
                // unlock the save button
                setDisabledActions(false);

                if (file) {
                  setFieldValue("attachment.fileName", file.name);
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  setUploading(true);
                  reader.onload = () => {
                    const formData = new FormData();
                    formData.append("file", file);
                    if (editAttachment) {
                      dispatch(updateGPLetterAttachment(orgUnitId, attachment.id, formData)).then(response => {
                        if (response.error === true) displayApiError(response, setStatus);
                        else onSucceed();
                      });
                    } else {
                      // save the attachment
                      dispatch(uploadGPLetterAttachment(orgUnitId, formData)).then(response => {
                        if (response.error === true) displayApiError(response, setStatus);
                        else {
                          setFieldValue("attachment.id", response.payload.id);
                          setStatus({ apiErrors: null });
                          setUploading(false);
                        }
                      });
                    }
                  };
                }
              }}
            />
          )}
          {!editAttachment && (
            <>
              <Field
                name="attachment.outputType"
                component={SelectInput}
                label={FileTypeLabel}
                options={gpLetterOutputTypes}
                disabled={editAttachment}
                required
              />
              <Field
                name="attachment.patientSMS"
                component={CheckboxInput}
                label={PatientSMSLabel}
                disabled={editAttachment}
              />
              <Field
                name="attachment.excludeFromPrintedReferral"
                component={CheckboxInput}
                label={excludeFromPrintedReferralLabel}
              />
            </>
          )}
          {!editAttachment && enableAutoGPLetterGeneration && (
            <>
              <Field
                name="attachment.autoGenerateForReferralStatuses"
                component={AutoComplete}
                label={autoGenerateForReferralStatusesLabel}
                options={autoGeneratedTypes}
                disabled={editAttachment}
                onChange={selectedValues => {
                  // Check if the "Rejected" workflow state is among the selected values
                  const hasRejectedWorkflowState = includesRejectedStatus(selectedValues);

                  // If "Rejected" is not selected, clear the rejection reason codes and hide the field
                  if (!hasRejectedWorkflowState) {
                    setFieldValue("attachment.autoGenerateForReasonsForRejectionCodes", []);
                    setShowReasonsForRejectionCodesField(false);
                    return;
                  }

                  // Otherwise, show the rejection reason codes field
                  setShowReasonsForRejectionCodesField(true);
                }}
                multiple
              />
              {enableLetterTemplateOnlyGenerateForReasonForRejection
                && showReasonsForRejectionCodesField
                && (
                  <Field
                    name="attachment.autoGenerateForReasonsForRejectionCodes"
                    component={AutoComplete}
                    label={autoGenerateForReasonsForRejectionCodesLabel}
                    options={autoGeneratedRejectionReasons}
                    disabled={editAttachment}
                    multiple
                  />
                )}
              <Field
                name="attachment.autoGenerateNoPatientSMSConsentOnly"
                component={CheckboxInput}
                disabled={isEmpty(values.attachment.autoGenerateForReferralStatuses)}
                label={autoGenerateNoPatientSMSConsentOnlyLabel}
              />
              <Field
                name="attachment.includeInHL7ReferralMessages"
                component={CheckboxInput}
                label={includeInHL7ReferralMessagesLabel}
              />
            </>
          )}
        </>
      )}
    />
  );
};

export default OrgUnitGPLetterTemplateForm;
